import { Controller } from "@hotwired/stimulus"

// Searchable combobox: wraps a hidden <select> with a text input + dropdown.
// Usage:
//   <div data-controller="combobox">
//     <select data-combobox-target="select" ...>
//       <option value="">Prompt...</option>
//       <option value="1">Foo</option>
//     </select>
//   </div>
export default class extends Controller {
  static targets = ["select"]
  static values = {
    createUrl: { type: String, default: "" }
  }

  connect() {
    this.buildUI()
    this.selectedIndex = -1
  }

  disconnect() {
    this.inputEl?.removeEventListener("input", this.onInput)
    this.inputEl?.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("click", this.onClickOutside)
    clearTimeout(this._createErrorTimer)
  }

  buildUI() {
    const select = this.selectTarget
    select.style.display = "none"
    select.removeAttribute("required")

    // Collect options (skip blank prompt)
    this.options = Array.from(select.options)
      .filter(o => o.value !== "")
      .map(o => ({ value: o.value, label: o.text }))

    // Hidden input to hold the required validation
    this.hiddenInput = document.createElement("input")
    this.hiddenInput.type = "hidden"
    this.hiddenInput.name = select.name
    this.hiddenInput.value = select.value
    if (select.dataset.required !== undefined || select.hasAttribute("required")) {
      this.hiddenInput.required = true
    }

    // Text input with search icon
    const inputWrap = document.createElement("div")
    inputWrap.className = "combobox-input-wrap"

    const icon = document.createElement("span")
    icon.className = "combobox-icon"
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z" /></svg>'

    this.inputEl = document.createElement("input")
    this.inputEl.type = "text"
    this.inputEl.placeholder = select.options[0]?.text || "Cerca..."
    this.inputEl.className = "input w-full combobox-input"
    this.inputEl.autocomplete = "off"

    inputWrap.appendChild(icon)
    inputWrap.appendChild(this.inputEl)

    // Pre-fill if editing
    if (select.value) {
      const current = this.options.find(o => o.value === select.value)
      if (current) this.inputEl.value = current.label
    }

    // Dropdown
    this.dropdown = document.createElement("ul")
    this.dropdown.className = "combobox-dropdown hidden"

    // Wrapper
    const wrapper = document.createElement("div")
    wrapper.className = "relative"
    wrapper.appendChild(this.hiddenInput)
    wrapper.appendChild(inputWrap)
    wrapper.appendChild(this.dropdown)

    select.parentNode.insertBefore(wrapper, select.nextSibling)

    // Events
    this.onInput = this.filter.bind(this)
    this.onKeydown = this.handleKeydown.bind(this)
    this.onClickOutside = this.closeOnOutsideClick.bind(this)

    this.inputEl.addEventListener("input", this.onInput)
    this.inputEl.addEventListener("keydown", this.onKeydown)
    this.inputEl.addEventListener("focus", () => this.filter())
    document.addEventListener("click", this.onClickOutside)
  }

  filter() {
    if (this.isCreating) return
    const query = this.inputEl.value.toLowerCase().trim()
    const matches = query === ""
      ? this.options
      : this.options.filter(o => o.label.toLowerCase().includes(query))

    this.selectedIndex = -1
    this.renderDropdown(matches)
  }

  renderDropdown(matches) {
    this.dropdown.innerHTML = ""
    const query = this.inputEl.value.trim()

    if (matches.length === 0) {
      const li = document.createElement("li")
      li.className = "combobox-empty"
      li.textContent = "Nessun risultato"
      this.dropdown.appendChild(li)
    } else {
      matches.forEach((opt) => {
        const li = document.createElement("li")
        li.className = "combobox-option"
        li.textContent = opt.label
        li.addEventListener("mousedown", (e) => {
          e.preventDefault()
          this.selectOption(opt)
        })
        this.dropdown.appendChild(li)
      })
    }

    // "Crea" option: shown when createUrl is set, query is non-empty, no exact match
    if (this.createUrlValue && query.length > 0) {
      const exactMatch = this.options.find(o => o.label.toLowerCase() === query.toLowerCase())
      if (!exactMatch) {
        const li = document.createElement("li")
        li.className = "combobox-create-option combobox-option"
        li.dataset.createOption = "true"
        li.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width:0.875rem;height:0.875rem;flex-shrink:0"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg><span>Crea "<strong>${this.escapeHtml(query)}</strong>"</span>`
        li.addEventListener("mousedown", (e) => {
          e.preventDefault()
          this.createAndSelect(query)
        })
        this.dropdown.appendChild(li)
      }
    }

    this.dropdown.classList.remove("hidden")
    // visibleOptions: only real options, not the create item (for keyboard nav)
    this.visibleOptions = matches
  }

  handleKeydown(e) {
    const items = this.dropdown.querySelectorAll(".combobox-option")
    if (!items.length) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
      this.highlightItem(items)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      this.highlightItem(items)
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (this.selectedIndex >= 0) {
        const selectedLi = items[this.selectedIndex]
        if (selectedLi?.dataset.createOption === "true") {
          this.createAndSelect(this.inputEl.value.trim())
        } else if (this.visibleOptions[this.selectedIndex]) {
          this.selectOption(this.visibleOptions[this.selectedIndex])
        }
      }
    } else if (e.key === "Escape") {
      this.closeDropdown()
    }
  }

  highlightItem(items) {
    items.forEach((li, i) => {
      if (i === this.selectedIndex) {
        li.classList.add("active")
        li.scrollIntoView({ block: "nearest" })
      } else {
        li.classList.remove("active")
      }
    })
  }

  selectOption(opt) {
    this.inputEl.value = opt.label
    this.hiddenInput.value = opt.value
    this.selectTarget.value = opt.value
    this.closeDropdown()
  }

  closeDropdown() {
    this.dropdown.classList.add("hidden")
    this.selectedIndex = -1
  }

  closeOnOutsideClick(e) {
    if (!this.element.contains(e.target)) {
      this.closeDropdown()

      // If text doesn't match any option, clear selection
      const match = this.options.find(o => o.label === this.inputEl.value)
      if (!match) {
        this.inputEl.value = ""
        this.hiddenInput.value = ""
        this.selectTarget.value = ""
      }
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  createAndSelect(name) {
    const createLi = this.dropdown.querySelector("[data-create-option='true']")
    if (createLi) {
      createLi.classList.add("loading")
      createLi.innerHTML = `<span>Creazione in corso…</span>`
    }

    this.isCreating = true
    const token = document.querySelector('meta[name="csrf-token"]')?.content || ""

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      // client-specific: other entities are out of scope (see spec)
      body: JSON.stringify({ client: { business_name: name } })
    })
      .then(res => {
        if (!res.ok) return res.json().then(data => Promise.reject(data))
        return res.json()
      })
      .then(data => {
        this.isCreating = false

        // Add option to the hidden <select>
        const select = this.selectTarget
        const newOption = document.createElement("option")
        newOption.value = data.id
        newOption.text = data.business_name
        select.appendChild(newOption)

        // Add to in-memory options list
        this.options.push({ value: String(data.id), label: data.business_name })

        // Select it
        this.selectOption({ value: String(data.id), label: data.business_name })
      })
      .catch((err) => {
        console.error("combobox createAndSelect error:", err)
        this.isCreating = false
        if (createLi) {
          createLi.classList.remove("loading")
          createLi.classList.add("error")
          createLi.innerHTML = `<span>Errore nella creazione — riprova</span>`
          this._createErrorTimer = setTimeout(() => {
            createLi.classList.remove("error")
            const query = this.inputEl.value.trim()
            createLi.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width:0.875rem;height:0.875rem;flex-shrink:0"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg><span>Crea "<strong>${this.escapeHtml(query)}</strong>"</span>`
            createLi.classList.remove("loading")
          }, 2500)
        }
      })
  }
}
